import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetAllTagsQuery, useUpdateClientTagsMutation } from '@app/api/Tags';
import type { TagItemData } from '@app/components/LeadCard/data';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { toTagItems } from '@app/shared/utils';

import type { BaseState } from './useBaseState';

type UseTagsParams = Pick<
  BaseState,
  | 'leadCardData'
  | 'refetchLeadCard'
  | 'refreshLatestHistory'
  | 'selectedBranchId'
  | 'selectedLeadId'
  | 'selectedLeadUserDetails'
>;

interface UseTagsResult {
  currentTags: TagItemData[];
  availableTags: TagItemData[];
  selectedTagIds: number[];
  isEditAvailable: boolean;
  isTagsModalOpen: boolean;
  isTagsLoading: boolean;
  isTagsSaving: boolean;
  openTagsModal(): void;
  closeTagsModal(): void;
  saveTags(): Promise<void>;
  selectTags(tagIds: number[]): void;
}

const toNumericTagIds = (tags: TagItemData[]): number[] =>
  tags
    .map(({ id }) => (typeof id === 'number' ? id : Number(id)))
    .filter(Number.isFinite);

const mergeTags = (
  baseTags: TagItemData[],
  currentTags: TagItemData[]
): TagItemData[] => {
  const tagsById = new Map<number, TagItemData>();

  [...baseTags, ...currentTags].forEach((tag) => {
    const numericId = typeof tag.id === 'number' ? tag.id : Number(tag.id);

    if (!Number.isFinite(numericId)) return;
    if (tagsById.has(numericId)) return;

    tagsById.set(numericId, tag);
  });

  return Array.from(tagsById.values());
};

export const useTags = ({
  leadCardData,
  refetchLeadCard,
  refreshLatestHistory,
  selectedBranchId,
  selectedLeadId,
  selectedLeadUserDetails,
}: UseTagsParams): UseTagsResult => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { userAccessRights } = useNewUserAccessRights();
  const { showNotification } = useNotification();

  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [updateClientTags, { isLoading: isTagsSaving }] =
    useUpdateClientTagsMutation();

  const currentTags = useMemo(
    () => selectedLeadUserDetails?.tags ?? [],
    [selectedLeadUserDetails?.tags]
  );

  const currentTagIds = useMemo(
    () => toNumericTagIds(currentTags),
    [currentTags]
  );
  const currentTagIdsKey = currentTagIds.join(',');

  const isEditAvailable =
    Boolean(leadCardData) && userAccessRights?.Clients?.IsTagsChange === true;

  const { data: tagsData, isFetching: isTagsLoading } = useGetAllTagsQuery(
    isTagsModalOpen && ActiveBranchId ? { ActiveBranchId } : skipToken
  );

  const availableTags = useMemo(
    () => mergeTags(toTagItems(tagsData), currentTags),
    [currentTags, tagsData]
  );

  useEffect(() => {
    if (!isTagsModalOpen) return;

    setSelectedTagIds(currentTagIds);
  }, [currentTagIdsKey, currentTagIds, isTagsModalOpen]);

  useEffect(() => {
    setIsTagsModalOpen(false);
    setSelectedTagIds([]);
  }, [selectedBranchId, selectedLeadId]);

  const openTagsModal = useCallback(() => {
    if (!isEditAvailable) return;

    setSelectedTagIds(currentTagIds);
    setIsTagsModalOpen(true);
  }, [currentTagIds, isEditAvailable]);

  const closeTagsModal = useCallback(() => {
    if (isTagsSaving) return;

    setSelectedTagIds(currentTagIds);
    setIsTagsModalOpen(false);
  }, [currentTagIds, isTagsSaving]);

  const saveTags = useCallback(async () => {
    const clientId = leadCardData?.ClientId ?? null;

    if (!clientId) {
      showNotification('Идентификатор клиента не найден', FRONT_WARNING);
      return;
    }

    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    try {
      await updateClientTags({
        ActiveBranchId,
        clientId,
        data: selectedTagIds,
      }).unwrap();

      showNotification('Теги успешно обновлены', SUCCESS);
      setIsTagsModalOpen(false);
      refetchLeadCard();
      // Запись о смене тегов приходит в историю тем же путём, что комментарий
      // и файл: событие WebSocket ненадёжно, поэтому свежий диапазон тянем сами.
      void refreshLatestHistory();
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    ActiveBranchId,
    leadCardData?.ClientId,
    refetchLeadCard,
    refreshLatestHistory,
    selectedTagIds,
    showNotification,
    updateClientTags,
  ]);

  return useMemo(
    () => ({
      currentTags,
      availableTags,
      selectedTagIds,
      isEditAvailable,
      isTagsModalOpen,
      isTagsLoading,
      isTagsSaving,
      openTagsModal,
      closeTagsModal,
      saveTags,
      selectTags: setSelectedTagIds,
    }),
    [
      availableTags,
      closeTagsModal,
      currentTags,
      isEditAvailable,
      isTagsLoading,
      isTagsModalOpen,
      isTagsSaving,
      openTagsModal,
      saveTags,
      selectedTagIds,
    ]
  );
};
